import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RiCalendarScheduleLine, RiCalendarScheduleFill } from "react-icons/ri";
import { RiFolderImageLine, RiFolderImageFill } from "react-icons/ri";
import { IoSettingsOutline, IoSettings } from "react-icons/io5";
import {
    BsMegaphone,
    BsFillMegaphoneFill,
    BsQuestionDiamond,
    BsQuestionDiamondFill,
} from "react-icons/bs";
import { RiHomeGearLine, RiHomeGearFill } from "react-icons/ri";
import { RiBarChartLine, RiBarChartFill } from "react-icons/ri";
import { RiFocus3Line, RiFocus3Fill } from "react-icons/ri";
import { RiAddCircleLine, RiAddCircleFill } from "react-icons/ri";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAddPostPageSwitch } from "../add-post/useAddPostPageEnabled";

const LONG_PRESS_THRESHOLD = 500; // ms

const SideBar = () => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [pressedIndex, setPressedIndex] = useState<number | null>(null);
    const touchStartTimeRef = useRef<number>(0);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useIsMobile(900);
    // Shown only once the switch has answered: an entry that appears and then vanishes (switch off) would be a
    // dead click. The route itself stays fail-open.
    const addPostSwitch = useAddPostPageSwitch();
    const addPostEnabled = addPostSwitch.resolved && addPostSwitch.enabled;

    const menuItems = [
        {
            name: "My Schedule",
            id: "scheduleButton",
            path: "/",
            icon: RiCalendarScheduleLine,
            filledIcon: RiCalendarScheduleFill,
            resetCalendar: true,
        },
        {
            name: "My Listings",
            id: "listingsButton",
            path: "/listings",
            icon: RiHomeGearLine,
            filledIcon: RiHomeGearFill,
            resetCalendar: false,
        },
        {
            name: "My Promotions",
            id: "promotionsButton",
            path: "/promotions",
            icon: BsMegaphone,
            filledIcon: BsFillMegaphoneFill,
            resetCalendar: false,
        },
        {
            name: "My Content",
            id: "contentButton",
            path: "/my-content",
            icon: RiFolderImageLine,
            filledIcon: RiFolderImageFill,
            resetCalendar: false,
        },
    ];

    // Five flex-1 tabs need ~432px of a 344-414px mobile bar, which clips the
    // last one off-screen, so Analytics rides with the desktop-only group.
    const analyticsItem = {
        name: "Analytics",
        id: "analyticsButton",
        path: "/analytics",
        icon: RiBarChartLine,
        filledIcon: RiBarChartFill,
        resetCalendar: false,
    };

    // Desktop group like Analytics: the phone bar only fits four tabs. On
    // mobile, My Niche is reached through Settings → My Niche.
    const nicheItem = {
        name: "My Niche",
        id: "nicheButton",
        path: "/my-niche",
        icon: RiFocus3Line,
        filledIcon: RiFocus3Fill,
        resetCalendar: false,
    };

    // Desktop group like My Niche; shown only while the Add Post page switch is on
    // (off, the calendar's Add Post button opens the old modal instead).
    const addPostItem = {
        name: "Add Post",
        id: "addPostButton",
        path: "/add-post",
        icon: RiAddCircleLine,
        filledIcon: RiAddCircleFill,
        resetCalendar: false,
    };

    const helpItem = {
        name: "Help",
        id: "helpButton",
        path: "/help",
        icon: BsQuestionDiamond,
        filledIcon: BsQuestionDiamondFill,
        resetCalendar: false,
    };

    const settingsItem = {
        name: "My Settings",
        id: "settingsButton",
        path: "/settings",
        icon: IoSettingsOutline,
        filledIcon: IoSettings,
    };

    // Global mouse move event that checks if the mouse is outside the sidebar
    useEffect(() => {
        const handleMouseMove = (event: MouseEvent): void => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target as Node)
            ) {
                setIsExpanded(false);
            }
        };

        document.addEventListener("mousemove", handleMouseMove);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    return (
        <div
            ref={sidebarRef}
            onMouseEnter={() => setIsExpanded(true)}
            // You can optionally remove this onMouseLeave if the ref-based listener suits your needs
            onMouseLeave={() => !isMobile && setIsExpanded(false)}
            className={`
                bg-primary
                text-white 
                transition-all 
                duration-500 
                ease-in-out
                overflow-hidden
                ${
                    isMobile
                        ? "fixed bottom-0 left-0 w-full h-20"
                        : `h-full ${isExpanded ? "w-64" : "w-16"}`
                }
                z-50
            `}
        >
            <div
                className={`
                    flex ${isMobile ? "flex-row" : "flex-col"}
                    ${isMobile ? "justify-around" : "justify-between"}
                    ${isMobile ? "items-center" : "items-start"}
                    h-full p-2
                `}
            >
                <div
                    className={`
                        flex ${isMobile ? "flex-row w-full" : "flex-col"}
                        ${isMobile ? "justify-around" : "items-start"}
                        ${isMobile ? "" : "space-y-8"}
                        w-full
                    `}
                >
                    {menuItems.map((item, index) => (
                        <div
                            key={index}
                            id={item.id}
                            className={`
                                flex ${isMobile ? "flex-col" : "flex-row"}
                                items-center cursor-pointer select-none
                                ${!isMobile ? "hover:bg-[#F0DFAF] hover:text-primary" : ""}
                                ${
                                    isMobile && pressedIndex === index
                                        ? "bg-[#F0DFAF] text-primary"
                                        : location.pathname === item.path
                                        ? "text-secondary"
                                        : "text-white"
                                }
                                p-2 rounded-md transition-colors duration-300 ease-in-out
                                outline-none focus:outline-none
                                ${isMobile ? "flex-1 justify-center" : "w-full"}
                                ${!isExpanded && !isMobile ? "justify-center" : ""}
                            `}
                            onClick={() => {
                                navigate(item.path, {
                                    state: item.resetCalendar
                                        ? { resetCalendar: true }
                                        : {},
                                });
                            }}
                            onTouchStart={() => {
                                touchStartTimeRef.current = Date.now();
                                setPressedIndex(index);
                            }}
                            onTouchEnd={() => {
                                if (Date.now() - touchStartTimeRef.current >= LONG_PRESS_THRESHOLD) {
                                    setPressedIndex(null);
                                }
                                // short tap: keep highlight until next touch
                            }}
                            onTouchCancel={() => setPressedIndex(null)}
                            onContextMenu={(e) => e.preventDefault()}
                            style={{
                                height: isMobile ? "100%" : "auto",
                                WebkitTapHighlightColor: "transparent",
                            }}
                        >
                            <div className="flex-shrink-0">
                                {location.pathname === item.path ? (
                                    <item.filledIcon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                ) : (
                                    <item.icon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                )}
                            </div>
                            {isMobile ? (
                                <span className="text-xs mt-1 text-center whitespace-nowrap">
                                    {item.name}
                                </span>
                            ) : (
                                isExpanded && (
                                    <span className="text-lg whitespace-nowrap transition-opacity duration-300 ease-in-out opacity-100 delay-300 ml-4">
                                        {item.name}
                                    </span>
                                )
                            )}
                        </div>
                    ))}
                </div>
                {/* Settings and Help Items */}
                {!isMobile && (
                    <div className="flex flex-col items-start w-full">
                        {addPostEnabled && (
                            <div
                                id={addPostItem.id}
                                className={`
                                    flex items-center w-full cursor-pointer hover:bg-lightSecondary ${
                                        location.pathname.startsWith(addPostItem.path)
                                            ? "text-secondary"
                                            : "text-white"
                                    }
                                    hover:text-primary p-4 rounded-md transition-colors duration-300 ease-in-out
                                    ${!isExpanded ? "justify-center" : ""}
                                `}
                                onClick={() => navigate(addPostItem.path)}
                            >
                                <div className="flex-shrink-0">
                                    {location.pathname.startsWith(addPostItem.path) ? (
                                        <addPostItem.filledIcon
                                            size={24}
                                            className="transition-transform duration-300 ease-in-out text-current"
                                        />
                                    ) : (
                                        <addPostItem.icon
                                            size={24}
                                            className="transition-transform duration-300 ease-in-out text-current"
                                        />
                                    )}
                                </div>
                                {isExpanded && (
                                    <span
                                        className="
                                            text-lg
                                            whitespace-nowrap
                                            transition-opacity
                                            duration-300
                                            ease-in-out
                                            opacity-100 delay-300
                                            ml-4
                                        "
                                    >
                                        {addPostItem.name}
                                    </span>
                                )}
                            </div>
                        )}
                        <div
                            id={nicheItem.id}
                            className={`
                                flex items-center w-full cursor-pointer hover:bg-lightSecondary ${
                                    location.pathname === nicheItem.path
                                        ? "text-secondary"
                                        : "text-white"
                                }
                                hover:text-primary p-4 rounded-md transition-colors duration-300 ease-in-out
                                ${!isExpanded ? "justify-center" : ""}
                            `}
                            onClick={() => navigate(nicheItem.path)}
                        >
                            <div className="flex-shrink-0">
                                {location.pathname === nicheItem.path ? (
                                    <nicheItem.filledIcon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                ) : (
                                    <nicheItem.icon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                )}
                            </div>
                            {isExpanded && (
                                <span
                                    className="
                                        text-lg
                                        whitespace-nowrap
                                        transition-opacity
                                        duration-300
                                        ease-in-out
                                        opacity-100 delay-300
                                        ml-4
                                    "
                                >
                                    {nicheItem.name}
                                </span>
                            )}
                        </div>
                        <div
                            id={analyticsItem.id}
                            className={`
                                flex items-center w-full cursor-pointer hover:bg-lightSecondary ${
                                    location.pathname === analyticsItem.path
                                        ? "text-secondary"
                                        : "text-white"
                                }
                                hover:text-primary p-4 rounded-md transition-colors duration-300 ease-in-out
                                ${!isExpanded ? "justify-center" : ""}
                            `}
                            onClick={() => navigate(analyticsItem.path)}
                        >
                            <div className="flex-shrink-0">
                                {location.pathname === analyticsItem.path ? (
                                    <analyticsItem.filledIcon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                ) : (
                                    <analyticsItem.icon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                )}
                            </div>
                            {isExpanded && (
                                <span
                                    className="
                                        text-lg 
                                        whitespace-nowrap
                                        transition-opacity 
                                        duration-300 
                                        ease-in-out
                                        opacity-100 delay-300
                                        ml-4
                                    "
                                >
                                    {analyticsItem.name}
                                </span>
                            )}
                        </div>
                        <div
                            id={helpItem.id}
                            className={`
                                flex items-center w-full cursor-pointer hover:bg-[#F0DFAF] ${
                                    location.pathname === helpItem.path
                                        ? "text-secondary"
                                        : "text-white"
                                }
                                hover:text-primary p-4 rounded-md transition-colors duration-300 ease-in-out
                                ${!isExpanded ? "justify-center" : ""}
                            `}
                            onClick={() => navigate(helpItem.path)}
                        >
                            <div className="flex-shrink-0">
                                {location.pathname === helpItem.path ? (
                                    <helpItem.filledIcon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                ) : (
                                    <helpItem.icon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                )}
                            </div>
                            {isExpanded && (
                                <span
                                    className="
                                        text-lg 
                                        whitespace-nowrap
                                        transition-opacity 
                                        duration-300 
                                        ease-in-out
                                        opacity-100 delay-300
                                        ml-4
                                    "
                                >
                                    {helpItem.name}
                                </span>
                            )}
                        </div>
                        <div
                            id={settingsItem.id}
                            className={`
                                flex items-center w-full cursor-pointer hover:bg-[#F0DFAF] ${
                                    location.pathname === settingsItem.path
                                        ? "text-secondary"
                                        : "text-white"
                                }
                                hover:text-primary p-4 rounded-md transition-colors duration-300 ease-in-out
                                ${!isExpanded ? "justify-center" : ""}
                            `}
                            onClick={() => navigate(settingsItem.path)}
                        >
                            <div className="flex-shrink-0">
                                {location.pathname === settingsItem.path ? (
                                    <settingsItem.filledIcon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                ) : (
                                    <settingsItem.icon
                                        size={24}
                                        className="transition-transform duration-300 ease-in-out text-current"
                                    />
                                )}
                            </div>
                            {isExpanded && (
                                <span
                                    className="
                                        text-lg 
                                        whitespace-nowrap
                                        transition-opacity 
                                        duration-300 
                                        ease-in-out
                                        opacity-100 delay-300
                                        ml-4
                                    "
                                >
                                    {settingsItem.name}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SideBar;
